import React from "react";
import { Breadcrumbs, Link, Typography, Grid } from "@mui/material";
import { NavigateNext as NavigateNextIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useStepData } from "../context";
import { availableSteps as stepsNames } from "..";

type BreadcrumbNavigatorProps = {
  children: React.ReactNode;
}

type BreadcrumbLinkItemProps = {
  step: string;
  index: number;
  currentStepIndex: number;
  onClick: (index: number) => void;
}

const BreadcrumbLinkItem: React.FC<BreadcrumbLinkItemProps> = ({
  step,
  index,
  currentStepIndex,
  onClick,
}) => {
  if (index === currentStepIndex) {
    return (
      <Typography color="primary" sx={{ textDecoration: "underline" }}>
        {step}
      </Typography>
    );
  }

  const linkSx =
    index < currentStepIndex ? { cursor: "pointer" } : { pointerEvents: "none", color: "text.disabled", cursor: "default" };

  return (
    <Link color="inherit" onClick={() => onClick(index)} sx={linkSx}>
      {step}
    </Link>
  );
};

const useEnsureUploadPath = (): void => {
  const navigate = useNavigate();
  // Navigate to the first step if current url is settings/imports not settings/imports/upload
  React.useEffect(() => {
    if (window.location.pathname === "/dash/manager/settings/imports") {
      navigate("/dash/manager/settings/imports/upload", { replace: true });
    }
  }, [navigate]);
};

const BreadcrumbNavigator: React.FC<BreadcrumbNavigatorProps> = ({ children }) => {
  const { viewer, stepName, setStepName } = useStepData();
  const currentStepIndex = stepsNames.findIndex((step) => stepName === step);
  const handleBreadcrumbClick = (index: number) => {
    if (index < currentStepIndex) {
      setStepName(stepsNames[index]);
    }
  };

  useEnsureUploadPath();

  if (!viewer) return null;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          {stepsNames.map((step, index) => (
            <BreadcrumbLinkItem
              key={index}
              step={step}
              index={index}
              currentStepIndex={currentStepIndex}
              onClick={handleBreadcrumbClick}
            />
          ))}
        </Breadcrumbs>
      </Grid>
      <Grid size={{ xs: 12 }}>
        {children}
      </Grid>
    </Grid>
  );
};

export default BreadcrumbNavigator;
